// @ts-strict-ignore
import { TFunction } from 'i18next'
import { uniq } from 'lodash-es'
import { UseFormSetError } from 'react-hook-form'
import { logSentryError } from '@node-space/utils'
import { PAYMENT_METHODS_LABELS } from 'pages/Contacts/constants'
import { USD_LOCAL_ONLY_PAYMENT_METHODS } from 'pages/Contacts/utils/constants.fiatInstruments'
import {
  Address,
  BaseErrorResponse,
  BeneficiaryDetails,
  BeneficiaryDetailsCurrency,
  BeneficiaryDetailsFiatDetails,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
  BeneficiaryType,
  PaymentMethods,
} from 'types/beneficiaries'
import {
  BankAccountPaymentInstrument,
  ContactEntityCompany,
  ContactEntityIndividual,
  ContactPaymentInstrument,
  ContactPaymentInstruments,
  EligibilityStatus,
  RelationshipType,
  WalletContactInstrumentEligibility,
} from 'types/contacts'
import { SourceWalletTypes } from 'types/move/move'
import { ConfirmPayeeResponse, OrchestratedConfirmPayeeName } from 'types/payoutsV2'
import { WalletType } from 'types/types'
import {
  UnifiedPayoutsBankAccount,
  UnifiedPayoutsV2Request,
} from 'types/unifiedPayouts/unifiedPayoutsV2'
import { WalletsPayRequest } from 'types/wallets'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { getBeneficiaryName } from 'utils/beneficiaries'
import track from 'utils/tracker'
import { sentenceCase } from 'utils/utils'
import { SourcePayWallet } from '../Pay/types'
import { ELIGIBILITY_MESSAGE_MAPPING, SEPA_METHODS } from './constants'
import { FiatSendFormData } from './form/fiatSendFormSchema'
import { LegacyFiatPayoutBeneficiaryDetails, LegacyFiatPayoutWalletDetails } from './types'

const AMOUNT_ERROR_MAPPING = {
  availableBalance: 'amount',
  maxPayoutAmount: 'amount',
  payoutAmount: 'amount',
  totalAmount: 'amount',
  fee: 'amount',
}

const FIELD_ERROR_MAPPING: Record<string, keyof FiatSendFormData> = {
  amount: 'amount',
  currency: 'currencyCode',
  walletId: 'sourceWalletLsid',
}

const IGNORED_FIELD_ERRORS = new Set([
  ...Object.keys(AMOUNT_ERROR_MAPPING),
  ...Object.keys(FIELD_ERROR_MAPPING),
  'beneficiary.remittanceInformation',
])

const getEntityDetails = (beneficiary: BeneficiaryDetails) => {
  const { entityType, firstName, lastName, businessName, dateOfBirth } = beneficiary || {}
  const isBusinessEntity = entityType === BeneficiaryEntityType.BUSINESS

  return {
    type: isBusinessEntity ? BeneficiaryEntityType.COMPANY : BeneficiaryEntityType.INDIVIDUAL,
    ...(isBusinessEntity
      ? {
          legalName: businessName || '',
        }
      : {
          firstName: firstName || '',
          lastName: lastName || '',
          ...(dateOfBirth && { dateOfBirth: dateOfBirth.replaceAll('/', '-') }),
        }),
    relationshipType:
      beneficiary?.beneficiaryType === BeneficiaryType.FIRST_PARTY
        ? BeneficiaryType.SELF_OWNED
        : BeneficiaryType.THIRD_PARTY,
  }
}

const getAddressObject = (address?: Address) => {
  if (!address) return undefined

  return {
    ...(address?.addressLine1 && { addressLine1: address.addressLine1 }),
    ...(address?.addressLine2 && { addressLine2: address.addressLine2 }),
    ...(address?.city && { city: address.city }),
    ...(address?.region && { region: address.region }),
    ...(address?.postCode && { postCode: address.postCode }),
    ...(address?.country && { country: address.country }),
  }
}

const getBankAccountDetails = (fiat: BeneficiaryDetailsFiatDetails): UnifiedPayoutsBankAccount => {
  return {
    accountNumber: fiat?.accountNumber || '',
    ...(fiat?.accountType && { accountType: fiat.accountType }),
    bank: {
      identificationCode: fiat?.code || '',
      ...(fiat?.nid && { nid: fiat.nid }),
      ...(fiat?.bankAddress && {
        address: getAddressObject(fiat.bankAddress),
      }),
    },
    ...(fiat?.intermediaryBank && {
      intermediaryBanks: getIntermediaryBanks(fiat),
    }),
  }
}

const getIntermediaryBanks = (fiat?: BeneficiaryDetailsFiatDetails) => {
  if (!fiat?.intermediaryBank) return

  const { intermediaryBank } = fiat
  return [
    {
      identificationCode: intermediaryBank?.code || '',
      name: intermediaryBank?.bankName || '',
      ...(intermediaryBank?.nid && { nid: intermediaryBank.nid }),
      ...(intermediaryBank?.bankAddress && {
        address: getAddressObject(intermediaryBank.bankAddress),
      }),
    },
  ]
}

const getContactBankAccountBankCodes = (
  formValues: FiatSendFormData,
  contactBankAccount: BankAccountPaymentInstrument,
  wallet: SourcePayWallet
) => {
  const { bank } = contactBankAccount || {}
  const { identificationCode, swiftCode } = bank || {}

  const isUSD = contactBankAccount?.currency === BeneficiaryDetailsCurrency.USD
  const isUSCountry = contactBankAccount?.bank?.address?.country === 'US'
  const isSwift = formValues?.paymentMethod === PaymentMethods.SWIFT

  const walletSupportsSwiftOnly =
    wallet?.supportedTransferDestinations?.includes(BeneficiaryTransferDestination.INTERNATIONAL) &&
    wallet?.supportedTransferDestinations?.length === 1
  const walletSupportsLocal = wallet?.supportedTransferDestinations?.includes(
    BeneficiaryTransferDestination.LOCAL
  )

  if (isUSD) {
    if (isSwift) {
      return isUSCountry
        ? {
            nid: identificationCode || '',
            identificationCode: swiftCode || '',
          }
        : {
            identificationCode: (walletSupportsSwiftOnly ? swiftCode : identificationCode) || '',
          }
    }

    return {
      ...(walletSupportsLocal && { identificationCode }),
      ...(walletSupportsSwiftOnly && { identificationCode: swiftCode || identificationCode }),
    }
  }

  return {
    ...(walletSupportsLocal && { identificationCode }),
    ...(walletSupportsSwiftOnly && { identificationCode: swiftCode || identificationCode }),
  }
}

interface GetUnifiedPayoutRequestV2WithContactBankAccountProps {
  formValues: FiatSendFormData
  selectedWallet: SourcePayWallet
  contactPaymentInstrument: ContactPaymentInstruments
}

const getAccountHolderAddress = (contactPaymentInstrument: ContactPaymentInstruments) => {
  const { paymentInstrument, contact } = contactPaymentInstrument || {}
  const { accountHolderAddress } = (paymentInstrument || {}) as BankAccountPaymentInstrument

  if (accountHolderAddress) {
    return getAddressObject(accountHolderAddress)
  }

  return getAddressObject(contact?.entity?.address)
}

const getPaymentMethod = (
  formValues: FiatSendFormData,
) => {

  const { paymentMethod, achSameDay } = formValues || {}

  if (achSameDay) {
    return PaymentMethods.ACH_SAME_DAY
  }

  return paymentMethod
}

export const getUnifiedPayoutRequestV2WithContactBankAccount = ({
  formValues,
  selectedWallet,
  contactPaymentInstrument,
}: GetUnifiedPayoutRequestV2WithContactBankAccountProps): UnifiedPayoutsV2Request => {
  const { paymentInstrument, contact } = contactPaymentInstrument || {}
  const { relationshipType } = contact || {}
  const contactBankAccount = paymentInstrument as BankAccountPaymentInstrument
  const { accountNumber, accountType, accountHolderName, bank, intermediaryBanks } =
    contactBankAccount || {}

  const isInternational = contactBankAccount?.paymentMethods?.includes(PaymentMethods.SWIFT)
  const isUSD = contactBankAccount?.currency === BeneficiaryDetailsCurrency.USD
  const isBusinessEntity = contact?.entity?.type === BeneficiaryEntityType.COMPANY

  const intermediaryBank = intermediaryBanks?.[0]
  const isIntermediaryBankInUS = intermediaryBank?.address?.country === 'US'

  const businessEntityName = contact?.entity as ContactEntityCompany
  const individualEntityName = contact?.entity as ContactEntityIndividual

  const entityAddress = getAccountHolderAddress(contactPaymentInstrument)
  const paymentMethod = getPaymentMethod(formValues)

  return {
    walletId: selectedWallet?.walletId || '',
    amount: Number(formValues?.amount),
    currency: selectedWallet?.currency || '',
    ...(paymentMethod && { method: paymentMethod }),
    beneficiary: {
      remittanceInformation: formValues?.paymentReference || '',
      entity: {
        type: isBusinessEntity ? BeneficiaryEntityType.COMPANY : BeneficiaryEntityType.INDIVIDUAL,
        ...(isBusinessEntity
          ? {
              legalName: accountHolderName?.legalName || businessEntityName?.legalName || '',
            }
          : {
              firstName: accountHolderName?.firstName || individualEntityName?.firstName || '',
              lastName: accountHolderName?.lastName || individualEntityName?.lastName || '',
            }),
        relationshipType:
          relationshipType === BeneficiaryType.SELF_OWNED
            ? RelationshipType.SELF_OWNED
            : BeneficiaryType.THIRD_PARTY,
        ...(entityAddress && {
          address: getAddressObject(entityAddress),
        }),
      },
      bankAccount: {
        accountNumber: accountNumber || '',
        ...(accountType && { accountType }),
        bank: {
          ...getContactBankAccountBankCodes(formValues, contactBankAccount, selectedWallet),
          ...(bank?.name && { name: bank.name }),
          ...(bank?.address && {
            address: getAddressObject(bank.address),
          }),
        },
        ...(intermediaryBank && {
          intermediaryBanks: [
            {
              ...(isUSD && isInternational && isIntermediaryBankInUS
                ? {
                    nid: intermediaryBank?.identificationCode,
                    identificationCode: intermediaryBank?.swiftCode,
                  }
                : {
                    identificationCode: intermediaryBank?.swiftCode,
                  }),
              ...(intermediaryBank?.name && { name: intermediaryBank.name }),
              ...(intermediaryBank?.address && {
                address: getAddressObject(intermediaryBank.address),
              }),
            },
          ],
        }),
      },
    },
  }
}

export const getUnifiedPayoutRequestV2 = (
  formValues: FiatSendFormData,
  selectedWallet: SourcePayWallet,
  fiatBeneficiary: BeneficiaryDetails
): UnifiedPayoutsV2Request => {
  const { fiat, address } = fiatBeneficiary || {}
  const paymentMethod = getPaymentMethod(formValues)

  return {
    walletId: selectedWallet?.walletId || '',
    amount: Number(formValues?.amount),
    currency: selectedWallet?.currency || '',
    ...(paymentMethod && {
      method: paymentMethod,
    }),
    beneficiary: {
      remittanceInformation: formValues?.paymentReference || '',
      entity: {
        ...getEntityDetails(fiatBeneficiary),
        ...(address && {
          address: getAddressObject(address),
        }),
      },
      bankAccount: getBankAccountDetails(fiat),
    },
  }
}

export const getLegacyPayoutRequest = (
  formValues: FiatSendFormData,
  selectedFiatBeneficiary: BeneficiaryDetails,
  twoFactorEnabled: boolean
): WalletsPayRequest => {
  const {
    fiat,
    address,
    addressCountry,
    entityType,
    transferDestination,
    firstName,
    lastName,
    dateOfBirth,
    businessName,
  } = selectedFiatBeneficiary || {}
  const { bankAddress, bankName, intermediaryBank, accountNumber, code } = fiat || {}

  const payRequest: WalletsPayRequest = {
    amount: Number(formValues?.amount),
    authorization: {
      ...(twoFactorEnabled && { totp: formValues.twoFactorCode }),
      ...(!twoFactorEnabled && { password: formValues.twoFactorCode }),
    },
    receiver: {
      ...(entityType && { type: entityType }),
      ...(addressCountry && { countryCode: addressCountry }),
      ...(formValues?.paymentReference && { reference: formValues?.paymentReference }),
      ...(transferDestination === BeneficiaryTransferDestination.INTERNATIONAL && {
        ...(address && { address }),
        ...(bankAddress && { bankAddress }),
        ...(intermediaryBank && { intermediaryBank }),
        ...(bankName && { bankName }),
      }),
      ...(accountNumber && { bankAccountNumber: accountNumber }),
      ...(code && { code }),
      ...(entityType === BeneficiaryEntityType.INDIVIDUAL && {
        firstName,
        lastName,
        dateOfBirth,
      }),
      ...(entityType === BeneficiaryEntityType.BUSINESS && {
        companyName: businessName,
      }),
    },
  }

  return payRequest
}

export const logLegacyFiatPayout = (
  wallet: LegacyFiatPayoutWalletDetails,
  beneficiary: LegacyFiatPayoutBeneficiaryDetails
) => {
  const eventName = beneficiary?.isThirdParty
    ? AmplitudeEvent.PAY_REQUEST
    : AmplitudeEvent.WITHDRAW_REQUEST

  logSentryError('Error from FiatSend - Legacy payout', {
    wallet,
    beneficiary,
  })

  track.Amp.track(eventName, {
    category: AmplitudeEventCategory.MERCHANT_PORTAL,
    action: AmplitudeEventAction.CLICK,
    version: 1,
    wallet,
    beneficiary,
  })
}

export const getConfirmationOfPayeeName = (
  beneficiary: BeneficiaryDetails,
  confirmationOfPayeeResponse: ConfirmPayeeResponse
): OrchestratedConfirmPayeeName => {
  const { actualName } = confirmationOfPayeeResponse || {}
  const { entityType } = beneficiary || {}
  const isBeneficiaryBusiness = entityType === BeneficiaryEntityType.BUSINESS

  if (!actualName) return {}

  if (isBeneficiaryBusiness) {
    return { businessName: actualName }
  }

  const names = actualName.split(' ')

  return {
    firstName: names?.[0],
    lastName: names?.slice(1).join(' '),
  }
}

export const logLegacyPayout = (
  selectedWallet: SourcePayWallet,
  fiatBeneficiary: BeneficiaryDetails,
  isLegacyPaymentMethod: boolean
) => {
  const wallet = {
    name: selectedWallet?.walletName,
    currency: selectedWallet?.currency,
    supportedTransferDestinations: selectedWallet?.supportedTransferDestinations,
    id: selectedWallet?.legacyWalletId,
    lsid: selectedWallet?.walletId,
  }

  const beneficiary = {
    name: getBeneficiaryName(fiatBeneficiary),
    alias: fiatBeneficiary?.alias,
    transferDestination: fiatBeneficiary?.transferDestination,
    currency: fiatBeneficiary?.currency,
    ...(isLegacyPaymentMethod && { id: Number(fiatBeneficiary?.reference) }),
    ...(!isLegacyPaymentMethod && { reference: fiatBeneficiary?.reference }),
    entityType: fiatBeneficiary?.entityType,
    isThirdParty: fiatBeneficiary?.beneficiaryType === BeneficiaryType.THIRD_PARTY,
  }

  logLegacyFiatPayout(wallet, beneficiary)
}

const getAmountError = (
  feeErrorResponse: BaseErrorResponse,
  setError: UseFormSetError<FiatSendFormData>,
  setFeeErrorMessages: (value: string[]) => void
): string | undefined => {
  const hasAmountError = Object.keys(AMOUNT_ERROR_MAPPING).some(
    key => feeErrorResponse?.data?.details?.errors?.[key]
  )

  if (!hasAmountError) {
    return
  }

  const { availableBalance, maxPayoutAmount, payoutAmount, totalAmount, fee } =
    feeErrorResponse?.data?.details?.errors || {}

  const feeOnlyError = fee && !availableBalance && !maxPayoutAmount && !payoutAmount && !totalAmount

  if (feeOnlyError) {
    const feeErrorList = feeErrorResponse?.data?.details?.errors?.fee || []
    const feeErrorMessage = feeErrorList?.map((error: string) => sentenceCase(error))

    if (feeErrorMessage?.length) {
      setError('amount', { message: feeErrorMessage.join('. ') })
      return feeErrorMessage.join('. ')
    }
  }

  if (availableBalance && maxPayoutAmount && payoutAmount && totalAmount) {
    setFeeErrorMessages([feeErrorResponse?.data?.details?.message])
    return feeErrorResponse?.data?.details?.message
  }
}

const getPaymentReferenceError = (feeErrorResponse: BaseErrorResponse): string | undefined => {
  const remittanceInformation =
    feeErrorResponse?.data?.details?.errors?.['beneficiary.remittanceInformation'] ||
    feeErrorResponse?.data?.details?.errors?.remittanceInformation

  if (remittanceInformation) {
    return remittanceInformation.map((error: string) => sentenceCase(error)).join('. ')
  }
}

export const setPayoutErrors = ({
  feeErrorResponse,
  setError,
  setFeeErrorMessages,
}: {
  feeErrorResponse: BaseErrorResponse
  setError: UseFormSetError<FiatSendFormData>
  setFeeErrorMessages: (value: string[]) => void
}) => {
  const fieldErrors = feeErrorResponse?.data?.details?.errors || {}
  const errorsKeys = Object.keys(fieldErrors)

  const amountError = getAmountError(feeErrorResponse, setError, setFeeErrorMessages)

  if (amountError) return

  const paymentReferenceError = getPaymentReferenceError(feeErrorResponse)
  if (paymentReferenceError) {
    setError('paymentReference', { message: paymentReferenceError })
    return
  }

  const generalErrorMessage =
    feeErrorResponse?.data?.details?.message ||
    feeErrorResponse?.data?.message ||
    feeErrorResponse?.message

  if (generalErrorMessage) {
    setFeeErrorMessages([generalErrorMessage])
  }

  const mappedFieldErrors = errorsKeys.filter((errorKey: string) => FIELD_ERROR_MAPPING[errorKey])
  mappedFieldErrors.forEach((errorKey: string) => {
    const formField = FIELD_ERROR_MAPPING[errorKey]
    const messages = fieldErrors[errorKey]
    setError(formField, { message: messages.join('. ') })
  })

  const nonAmountOrPaymentReferenceErrors = errorsKeys.filter(
    (errorKey: string) => !IGNORED_FIELD_ERRORS.has(errorKey)
  )
  if (nonAmountOrPaymentReferenceErrors?.length) {
    const errorMessages = nonAmountOrPaymentReferenceErrors
      .flatMap((errorKey: string) => fieldErrors[errorKey])
      .map((error: string) => sentenceCase(error))
    setFeeErrorMessages(errorMessages)
  }
}

// Helper function to create SourcePayWallet from WalletType
export const createSourcePayWallet = (
  wallet: WalletType,
  walletType: (typeof SourceWalletTypes)[keyof typeof SourceWalletTypes]
): SourcePayWallet => ({
  walletId: wallet?.lsid,
  legacyWalletId: wallet?.id,
  walletName: wallet?.description,
  currency: wallet?.currency?.code,
  balance: wallet?.available,
  walletType,
  supportedTransferDestinations: wallet?.supportedTransferDestinations,
  supportsInternalBvnkNetworkTransfers: wallet?.supportsInternalBvnkNetworkTransfers,
  supportsFirstPartyPayouts: wallet?.supportsWithdrawals,
  supportsThirdPartyPayouts: wallet?.supportsThirdParty,
  supportsPayins: wallet?.supportsDeposits,
})

// Helper function to get payment methods based on wallet currency and destinations
export const getPaymentMethodsForCurrency = (
  currency: string,
  supportedTransferDestinations?: BeneficiaryTransferDestination[]
): Array<(typeof PaymentMethods)[keyof typeof PaymentMethods]> => {
  const isSwiftWalletOnly =
    supportedTransferDestinations?.includes(BeneficiaryTransferDestination.INTERNATIONAL) &&
    supportedTransferDestinations?.length === 1

  if (isSwiftWalletOnly) {
    return [PaymentMethods.SWIFT]
  }

  const paymentMethods: Array<(typeof PaymentMethods)[keyof typeof PaymentMethods]> = []

  switch (currency) {
    case 'USD':
      paymentMethods.push(
        PaymentMethods.ACH,
        PaymentMethods.FEDWIRE,
        PaymentMethods.CRNOW,
        PaymentMethods.CUBIX,
        PaymentMethods.FEDNOW,
        PaymentMethods.RTP
      )
      break
    case 'EUR':
      paymentMethods.push(PaymentMethods.SEPA, PaymentMethods.SEPA_INSTANT)
      break
    case 'GBP':
      paymentMethods.push(PaymentMethods.FASTER_PAYMENT)
      break
    default:
      break
  }

  if (
    supportedTransferDestinations &&
    supportedTransferDestinations.length > 1 &&
    supportedTransferDestinations.includes(BeneficiaryTransferDestination.INTERNATIONAL)
  ) {
    paymentMethods.push(PaymentMethods.SWIFT)
  }

  return paymentMethods
}

export const getIneligibleBankAccountMessage = (
  t: TFunction,
  currency: string,
  eligibility: WalletContactInstrumentEligibility[] = []
) => {
  const isEligible = eligibility?.some(e => e.status === EligibilityStatus.ELIGIBLE)

  if (isEligible) {
    return
  }

  const ineligibleReasons = eligibility?.filter(e => e.status === EligibilityStatus.INELIGIBLE)

  const unavailablePaymentMethods = ineligibleReasons
    ?.filter(e => e.reasons.includes('PAYMENT_METHOD_UNAVAILABLE'))
    ?.map(e => e.paymentMethod)

  const otherReasonCodes = ineligibleReasons?.reduce(
    (codes: string[], currentEligibility: WalletContactInstrumentEligibility) => {
      const nonUnavailableReasons = currentEligibility?.reasons?.filter(
        reason => reason !== 'PAYMENT_METHOD_UNAVAILABLE'
      )
      return [...codes, ...nonUnavailableReasons]
    },
    []
  )

  const messages: string[] = []

  if (unavailablePaymentMethods?.length) {
    let methodsToDisplay: string[]

    if (currency === 'EUR') {
      const hasSepa = unavailablePaymentMethods.some(paymentMethod =>
        SEPA_METHODS.has(paymentMethod)
      )
      const nonSepaMethods = unavailablePaymentMethods.filter(
        paymentMethod => !SEPA_METHODS.has(paymentMethod)
      )
      methodsToDisplay = [...(hasSepa ? [PaymentMethods.SEPA] : []), ...nonSepaMethods]
    } else {
      methodsToDisplay = unavailablePaymentMethods
    }

    const paymentMethodNames = methodsToDisplay.map(
      method => t(PAYMENT_METHODS_LABELS[method]) || method
    )
    const combinedMethods =
      paymentMethodNames.length > 1
        ? `${paymentMethodNames.slice(0, -1).join(', ')} and ${paymentMethodNames.at(-1)}`
        : paymentMethodNames[0]

    messages.push(
      t(ELIGIBILITY_MESSAGE_MAPPING.PAYMENT_METHOD_UNAVAILABLE, {
        paymentMethods: combinedMethods,
      })
    )
  }

  const otherMessages = uniq(otherReasonCodes)
    ?.filter(reasonCode => !!t(ELIGIBILITY_MESSAGE_MAPPING[reasonCode]))
    ?.map(reasonCode => t(ELIGIBILITY_MESSAGE_MAPPING[reasonCode]))

  if (otherMessages?.length) {
    messages.push(...otherMessages)
  }

  return messages.join(' ')
}

const getAvailableEurMethods = (
  walletTransferDestinations: BeneficiaryTransferDestination[],
  bankAccountPaymentMethods: string[]
): string[] => {
  const walletSupportsLocal = walletTransferDestinations?.includes(
    BeneficiaryTransferDestination.LOCAL
  )
  const walletSupportsInternational = walletTransferDestinations?.includes(
    BeneficiaryTransferDestination.INTERNATIONAL
  )

  const bankHasSepa = bankAccountPaymentMethods?.some(m => SEPA_METHODS.has(m))
  const bankHasSwift = bankAccountPaymentMethods?.includes(PaymentMethods.SWIFT)

  return [
    ...(walletSupportsLocal && bankHasSepa ? [PaymentMethods.SEPA, PaymentMethods.SEPA_INSTANT] : []),
    ...(walletSupportsInternational && bankHasSwift ? [PaymentMethods.SWIFT] : []),
  ]
}

const getAvailableUsdMethods = (
  walletTransferDestinations: BeneficiaryTransferDestination[],
  bankAccountPaymentMethods: string[]
): string[] => {
  const walletSupportsLocal = walletTransferDestinations?.includes(
    BeneficiaryTransferDestination.LOCAL
  )
  const walletSupportsInternational = walletTransferDestinations?.includes(
    BeneficiaryTransferDestination.INTERNATIONAL
  )

  const localPaymentMethods = walletSupportsLocal
    ? bankAccountPaymentMethods?.filter(paymentMethod =>
        USD_LOCAL_ONLY_PAYMENT_METHODS.includes(paymentMethod)
      ) ?? []
    : []

  const isSwiftSupported = bankAccountPaymentMethods?.includes(PaymentMethods.SWIFT) && walletSupportsInternational

  return [
    ...localPaymentMethods,
    ...(isSwiftSupported ? [PaymentMethods.SWIFT] : []),
  ]
}

const getAvailableGbpMethods = (
  walletTransferDestinations: BeneficiaryTransferDestination[],
  bankAccountPaymentMethods: string[]
): string[] => {
  const walletSupportsLocal = walletTransferDestinations?.includes(
    BeneficiaryTransferDestination.LOCAL
  )
  const walletSupportsInternational = walletTransferDestinations?.includes(
    BeneficiaryTransferDestination.INTERNATIONAL
  )

  const isLocalSupported = walletSupportsLocal && bankAccountPaymentMethods?.includes(PaymentMethods.FASTER_PAYMENT)
  const isSwiftSupported = bankAccountPaymentMethods?.includes(PaymentMethods.SWIFT) && walletSupportsInternational

  return [
    ...(isLocalSupported ? [PaymentMethods.FASTER_PAYMENT] : []),
    ...(isSwiftSupported ? [PaymentMethods.SWIFT] : []),
  ]
}

export const getAvailablePaymentMethods = (
  currency: string,
  walletTransferDestinations: BeneficiaryTransferDestination[],
  bankAccountPaymentMethods: string[]
): string[] => {
  switch (currency) {
    case 'EUR':
      return getAvailableEurMethods(walletTransferDestinations, bankAccountPaymentMethods)
    case 'USD':
      return getAvailableUsdMethods(walletTransferDestinations, bankAccountPaymentMethods)
    case 'GBP':
      return getAvailableGbpMethods(walletTransferDestinations, bankAccountPaymentMethods)
    default:
      return []
  }
}

export const getMappedUpdatedPaymentInstrument = (
  // Refactor interface ContactPaymentInstruments to PaymentInstrument
  currentPaymentInstrument: ContactPaymentInstruments,
  updatedPaymentInstrument: ContactPaymentInstrument
): ContactPaymentInstruments => {
  const { eligibility, contact } = currentPaymentInstrument || {}

  return {
    contact,
    eligibility,
    paymentInstrument: updatedPaymentInstrument,
  }
}
